import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { Sparkles } from "lucide-react";

export default function Login() {
  const { user, login, setUser } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [reg, setReg] = useState({
    first_name: "", last_name: "", email: "", password: "",
    company_name: "", company_address: "", company_phone: "", company_email: "",
    hourly_rate: 100,
  });

  if (user) return <Navigate to="/dashboard" replace />;

  const submitLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("auth.invalid"));
    } finally { setLoading(false); }
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", {
        user: { first_name: reg.first_name, last_name: reg.last_name, email: reg.email, password: reg.password },
        company: { name: reg.company_name, address: reg.company_address, phone: reg.company_phone, email: reg.company_email, hourly_rate: Number(reg.hourly_rate) || 100 },
      });
      if (data.token) localStorage.setItem("autoflow_token", data.token);
      setUser(data.user);
      toast.success(reg.company_name);
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  const submitDemo = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/demo");
      if (data.token) localStorage.setItem("autoflow_token", data.token);
      setUser(data.user);
      toast.success("Demo");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen app-bg flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-5xl grid md:grid-cols-2 gap-0 surface rounded-2xl overflow-hidden">
        <div className="hidden md:block relative bg-cover bg-center"
          style={{ backgroundImage: "linear-gradient(135deg, rgba(15,17,21,0.85), rgba(15,17,21,0.5)), url('https://images.unsplash.com/photo-1551522435-a13afa10f103?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxjbGFzc2ljJTIwYXV0b21vdGl2ZSUyMHdvcmtzaG9wfGVufDB8fHx8MTc4MDg2MjE5MHww&ixlib=rb-4.1.0&q=85')" }}>
          <div className="absolute inset-0 p-10 flex flex-col justify-between">
            <Logo size={80} />
            <div>
              <h1 className="font-display text-3xl lg:text-4xl font-semibold leading-tight whitespace-pre-line" data-testid="login-hero-title">{t("auth.hero_title")}</h1>
              <p className="text-sm text-zinc-400 mt-3 max-w-sm" data-testid="login-hero-subtitle">{t("auth.hero_subtitle")}</p>
            </div>
          </div>
        </div>
        <div className="p-8 md:p-12 flex flex-col justify-center">
          <div className="md:hidden mb-6 flex justify-center"><Logo size={64} /></div>
          <Tabs defaultValue="login" className="space-y-4">
            <TabsList className="bg-[#171B24] border border-[#2E3241] grid grid-cols-2 w-full">
              <TabsTrigger value="login" data-testid="tab-login">{t("auth.signin")}</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">{t("auth.register")}</TabsTrigger>
            </TabsList>
            <TabsContent value="login">
              <form onSubmit={submitLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs uppercase tracking-wider text-zinc-400">{t("auth.email")}</Label>
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" className="bg-[#171B24] border-[#2E3241] text-white" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-xs uppercase tracking-wider text-zinc-400">{t("auth.password")}</Label>
                  <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" className="bg-[#171B24] border-[#2E3241] text-white" />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-blue-500 hover:bg-blue-600 text-white" data-testid="login-submit">
                  {loading ? "..." : t("auth.login_btn")}
                </Button>
              </form>
            </TabsContent>
            <TabsContent value="register">
              <form onSubmit={submitRegister} className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder={t("auth.first_name")} value={reg.first_name} onChange={(e) => setReg({ ...reg, first_name: e.target.value })} required className="bg-[#171B24] border-[#2E3241]" data-testid="reg-first-name" />
                  <Input placeholder={t("auth.last_name")} value={reg.last_name} onChange={(e) => setReg({ ...reg, last_name: e.target.value })} required className="bg-[#171B24] border-[#2E3241]" data-testid="reg-last-name" />
                </div>
                <Input type="email" placeholder={t("auth.email")} value={reg.email} onChange={(e) => setReg({ ...reg, email: e.target.value })} required className="bg-[#171B24] border-[#2E3241]" data-testid="reg-email" />
                <Input type="password" placeholder={t("auth.password")} value={reg.password} onChange={(e) => setReg({ ...reg, password: e.target.value })} required minLength={6} className="bg-[#171B24] border-[#2E3241]" data-testid="reg-password" />
                <div className="pt-2 border-t border-[#2E3241]" />
                <Input placeholder={t("auth.company_name")} value={reg.company_name} onChange={(e) => setReg({ ...reg, company_name: e.target.value })} required className="bg-[#171B24] border-[#2E3241]" data-testid="reg-company-name" />
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder={t("common.phone")} value={reg.company_phone} onChange={(e) => setReg({ ...reg, company_phone: e.target.value })} className="bg-[#171B24] border-[#2E3241]" />
                  <Input type="email" placeholder={t("common.email")} value={reg.company_email} onChange={(e) => setReg({ ...reg, company_email: e.target.value })} className="bg-[#171B24] border-[#2E3241]" />
                </div>
                <Input placeholder={t("common.address")} value={reg.company_address} onChange={(e) => setReg({ ...reg, company_address: e.target.value })} className="bg-[#171B24] border-[#2E3241]" />
                <div className="flex items-center gap-2">
                  <Input type="number" step="0.01" min="0"
                    placeholder={t("auth.hourly_rate_placeholder")}
                    value={reg.hourly_rate}
                    onChange={(e) => setReg({ ...reg, hourly_rate: e.target.value })}
                    className="bg-[#171B24] border-[#2E3241] flex-1"
                    data-testid="reg-hourly-rate" />
                  <span className="text-xs text-zinc-500 whitespace-nowrap">€/{t("common.hour_short")}</span>
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-blue-500 hover:bg-blue-600 text-white" data-testid="register-submit">
                  {loading ? "..." : t("auth.create_workspace")}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
          <div className="mt-6 pt-6 border-t border-[#2E3241]">
            <Button onClick={submitDemo} disabled={loading} variant="ghost"
              className="w-full bg-[#171B24] hover:bg-[#2A2F3A] text-zinc-300 border border-[#2E3241] gap-2"
              data-testid="demo-btn">
              <Sparkles className="h-4 w-4 text-amber-400" />
              {t("auth.demo_account")}
            </Button>
            <p className="text-[10px] text-zinc-500 mt-2 text-center">{t("auth.demo_hint")}</p>
          </div>
          <div className="mt-6 flex items-center gap-2 text-xs text-zinc-500 justify-center">
            <button onClick={() => i18n.changeLanguage("de")} data-testid="login-lang-de" className="hover:text-white">DE</button>
            <span>·</span>
            <button onClick={() => i18n.changeLanguage("en")} data-testid="login-lang-en" className="hover:text-white">EN</button>
            <span>·</span>
            <button onClick={() => i18n.changeLanguage("ro")} data-testid="login-lang-ro" className="hover:text-white">RO</button>
          </div>
        </div>
      </div>
    </div>
  );
}
